export const FC_V5_PROTOCOL = 'fc-v5' as const;

export type FcV5Protocol = typeof FC_V5_PROTOCOL;

export interface FcV5ClientCapabilities {
  audio_seq_header: true;
  client_audio_seq_header: true;
  client_audio_magic_header: true;
  playback_ack_v2: true;
  mic_open_state: true;
  capture_heartbeat: true;
  client_metrics: true;
}

export const FC_V5_CLIENT_CAPABILITIES: FcV5ClientCapabilities = {
  audio_seq_header: true,
  client_audio_seq_header: true,
  client_audio_magic_header: true,
  playback_ack_v2: true,
  mic_open_state: true,
  capture_heartbeat: true,
  client_metrics: true,
};

export interface FcV5QuestionIdentity {
  question_id?: string;
  question_index?: number;
  question_text?: string;
  knowledge_id?: string;
  knowledge_name?: string;
}

export interface FcV5AssistantTurnSnapshot {
  assistant_turn_id?: string;
  state?: 'started' | 'streaming' | 'logical_complete' | 'playback_acknowledged' | 'cancelled' | 'stalled' | string;
  audio_seq_end?: number;
}

export interface FcV5StateSnapshot {
  phase?: string;
  mic_open?: boolean;
  current_question?: FcV5QuestionIdentity | null;
  assistant_turn?: FcV5AssistantTurnSnapshot | null;
  progress?: {
    completed_questions?: number;
    total_questions?: number;
  };
}

export interface FcV5WireEvent {
  type: string;
  protocol?: FcV5Protocol;
  event_id?: string;
  server_seq?: number;
  sent_at?: string;
  requires_ack?: boolean;
  attempt_id?: string;
  provider_epoch?: number;
  assistant_turn_id?: string;
  question_id?: string;
  question_index?: number;
  question_idx?: number;
  question_text?: string;
  knowledge_id?: string;
  knowledge_name?: string;
  total_questions?: number;
  /** Solo en next_question — segundos de piense antes de habilitar respuesta (default 60 si no viaja). */
  thinking_time_s?: number;
  /** Solo en next_question — segundos de presupuesto de respuesta, ya saneados por el agente. */
  answer_budget_s?: number;
  /** Solo en mic_open (fc-affect-adaptive-tuning) — segundos de silencio tras los que el agente cierra el turno (valor real del settle, varia 2.0-5.5s). Opcional: sin el, la barra de silencio de turno del FE no se muestra. */
  turn_silence_s?: number;
  audio_seq_start?: number;
  audio_seq_end?: number;
  phase?: string;
  mic_open?: boolean;
  snapshot?: FcV5StateSnapshot;
  /** Solo en interview_complete (CONTRATO-VC-DETECTION.md §4.3) — 'conferencing_tool'
   *  cuando el agente suspendio por deteccion de videoconferencia. Aditivo/opcional:
   *  el BE ya mergeado confirma que viaja NULL explicito (no ausente) en cualquier
   *  otra terminacion — no asumir que la sola ausencia es significativa. `reason`
   *  (enum cerrado de siempre) no cambio. */
  termination_reason?: string | null;
  [key: string]: unknown;
}

export function isFcV5WireEvent(payload: unknown): payload is FcV5WireEvent {
  return !!payload
    && typeof payload === 'object'
    && (payload as { protocol?: unknown }).protocol === FC_V5_PROTOCOL
    && typeof (payload as { type?: unknown }).type === 'string';
}

/** kind del nivel del VcScorerService que dispara un client_proctoring_event —
 *  CONTRATO-VC-DETECTION.md §2 / ADENDA. */
export type FcV5ProctoringKind =
  | 'conferencing_suspect'
  | 'conferencing_confirmed'
  | 'conferencing_cleared';

/**
 * Mensaje outbound NUEVO (FE→BE) — VC detection (CONTRATO-VC-DETECTION.md §4.3 /
 * ADENDA). Debe matchear BYTE A BYTE protocol/inbound.py InboundClientProctoringEvent
 * del lado Python, ya mergeado (Pydantic `extra="forbid"`, `protocol` requerido tras
 * la correccion post-merge). A diferencia del resto de los `client_*` de este
 * archivo (client_question_timer, client_speech_state), este NO lleva `ts`: son
 * estos 6 campos y nada mas, o Pydantic rechaza el mensaje ENTERO y en silencio.
 * Ver fc-session.service.ts#sendProctoringEvent y envelope.schema.json
 * ($defs.client_proctoring_event_outbound, documentado pero fuera del oneOf
 * inbound de ese archivo).
 */
export interface FcV5ClientProctoringEvent {
  type: 'client_proctoring_event';
  protocol: FcV5Protocol;
  kind: FcV5ProctoringKind;
  score: number;
  families: string[];
  signals: string[];
}
